import React, { useCallback, useEffect, useMemo, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Typography } from '@kep-ui-kit/ui-components';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet';
import { withErrorHandling, IWrappedComponentProps } from '@selfserve/react-hocs';
import {
  storage,
  storageKeys,
  useAuth,
  useGoogleOAuth,
  useLog,
  usePost,
  useQueryParams,
  useStorage,
  GoogleButtonText,
  GoogleOAuthResult,
} from '@selfserve/react-hooks';
import {
  StyledLogo,
  GoogleButton,
  StyledV2PageWrapper,
  StyledV2PageBackground,
  StyledV2GlassCard,
  StyledV2Input,
  StyledV2Link,
  StyledV2LinkButton,
  StyledV2Checkbox,
  StyledV2SubmitBtn,
} from '@selfserve/react-ui';
import {
  routesPaths,
  responseTypes,
  socialType,
  apiEndpoints,
  links,
  buttonIds,
  getRecaptchaToken,
  subscriptionTypes,
} from '@selfserve/react-utils';
import { usePackageByProductFamilyRequest } from '@selfserve/react-common-data';

const useLoginRequest = () => usePost<any, any>(apiEndpoints.login);
const useLoginByEmailRequest = () => usePost<any, any>(apiEndpoints.loginByEmail);
const useSocialLoginRequest = () => usePost<any, any>(apiEndpoints.socialLogin);

export interface LoginV2FormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

const StyledFormArea = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const StyledFieldGroup = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

const StyledRememberRow = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

// Button size XL which not exists in the current version of @kep-ui-kit/ui-components
const StyledSecondaryBtn = styled(Button)(({ theme }: { theme?: any }) => ({
  fontSize: '16px',
  height: '48px',
  padding: '16px',
  backgroundColor: theme.palette.grey['600'],
  color: theme.palette.text.primary,
  '&:hover': { backgroundColor: theme.palette.grey['500'] },
  '&:disabled, &.Mui-disabled': {
    backgroundColor: theme.palette.grey['600'],
    color: theme.palette.grey['300'],
  },
}));

const StyledDividerRow = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
}));

const StyledDividerLine = styled.div(({ theme }: { theme?: any }) => ({
  flex: 1,
  height: '1px',
  backgroundColor: theme.palette.grey['500'],
}));

interface LoginV2Props extends IWrappedComponentProps {}

const LoginV2Component: React.FC<LoginV2Props> = ({ openErrorModal }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setToken } = useAuth();
  const { log } = useLog();
  const queryParams = useQueryParams();
  const productFamily = queryParams['productFamily'];
  const [, setSessionEmail] = useStorage(storage.SESSION, storageKeys.EMAIL, '');
  const [passwordlessMode, setPasswordlessMode] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [packageNameByProductFamily, setPackageNameByProductFamily] = useState('');

  const { data: packageData, isError: packageRequestError } = usePackageByProductFamilyRequest(
    productFamily || ''
  );

  useEffect(() => {
    if (packageData?.data && !packageRequestError && String(packageData?.status).startsWith(responseTypes.SUCCESS)) {
      const packageName: string =
        packageData.data?.find((pkg: any) => pkg.type === subscriptionTypes.FREE)?.name || '';
      setPackageNameByProductFamily(packageName);
    }
  }, [packageData, packageRequestError]);

  const {
    data: loginResponse,
    mutateAsync: handleLoginRequest,
    isLoading: isLoginLoading,
    isSuccess: isLoginSuccess,
  } = useLoginRequest();

  const {
    data: loginByEmailResponse,
    mutateAsync: handleLoginByEmailRequest,
    isLoading: isLoginByEmailLoading,
    variables: loginByEmailVariables,
  } = useLoginByEmailRequest() as any;

  const { mutateAsync: handleSocialLoginRequest, isLoading: isSocialLoginLoading } =
    useSocialLoginRequest();

  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);

  const isLoading =
    isLoginLoading || isLoginByEmailLoading || isSocialLoginLoading || isRecaptchaLoading;

  const validationSchema = useMemo(
    () =>
      passwordlessMode
        ? Yup.object().shape({
            email: Yup.string()
              .email(t('common.validations.email-valid'))
              .required(t('common.validations.email-required')),
          })
        : Yup.object().shape({
            email: Yup.string()
              .email(t('common.validations.email-valid'))
              .required(t('common.validations.email-required')),
            password: Yup.string().required(t('common.validations.password-required')),
          }),
    [t, passwordlessMode]
  );

  const formik: FormikProps<LoginV2FormValues> = useFormik<LoginV2FormValues>({
    validateOnChange,
    initialValues: { email: '', password: '', rememberMe: false },
    validationSchema,
    onSubmit: async ({ email, password }) => {
      if (isLoading) return;
      setIsRecaptchaLoading(true);
      try {
        const recaptchaToken = await getRecaptchaToken();
        if (passwordlessMode) {
          handleLoginByEmailRequest({
            email,
            recaptchaToken,
            ...(productFamily && { productFamily }),
          });
        } else {
          handleLoginRequest({
            email,
            password,
            recaptchaToken,
            ...(productFamily && { productFamily }),
          });
        }
      } catch {
        openErrorModal();
      } finally {
        setIsRecaptchaLoading(false);
      }
    },
  });

  const { errors, values, touched, handleChange, handleBlur, handleSubmit, setFieldValue } =
    formik;

  const handleSignupLinkClick = useCallback(
    (link: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (productFamily && packageNameByProductFamily) {
        navigate(routesPaths.GET_STARTED_V2.replace(':desiredPackageId', packageNameByProductFamily));
        return;
      }

      if (link) {
        if (link.includes('http')) {
          window.open(link, '_blank');
        } else {
          navigate(link);
        }
      }
    },
    [navigate, productFamily, packageNameByProductFamily]
  );

  const handleTogglePasswordlessMode = useCallback(() => {
    setPasswordlessMode((v) => !v);
    setFieldValue('password', '');
    setValidateOnChange(false);
  }, [setFieldValue]);

  useEffect(() => {
    const hasError = !!Object.keys(errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors]);

  useEffect(() => {
    if (!isLoginSuccess) return;
    const { status, data } = loginResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      setToken(data?.access_token as string, values.rememberMe);
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        navigate(routesPaths.MANAGEMENT_OVERVIEW);
      }
    }
    if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
      const { id } = data || {};
      if (id && i18n.exists(`errors.${id}`)) {
        const text = t(`errors.${id}`);
        const title = i18n.exists(`error-titles.${id}`) ? t(`error-titles.${id}`) : '';
        openErrorModal({ text, title });
      } else {
        openErrorModal();
      }
    }
  }, [loginResponse, isLoginSuccess]);

  useEffect(() => {
    const { status } = loginByEmailResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      setSessionEmail(loginByEmailVariables?.email);
      navigate(
        productFamily
          ? `${routesPaths.CHECK_YOUR_INBOX_LOGIN_V2}?productFamily=${productFamily}`
          : routesPaths.CHECK_YOUR_INBOX_LOGIN_V2
      );
    }
    if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
      openErrorModal();
    }
  }, [loginByEmailResponse]);

  const handleGoogleSuccess = useCallback(
    (result: GoogleOAuthResult) => {
      getRecaptchaToken()
        .then((recaptchaToken: string) =>
          handleSocialLoginRequest({
            type: socialType.GOOGLE,
            data: { token: result.token },
            recaptchaToken,
            ...(queryParams['productFamily'] && { productFamily: queryParams['productFamily'] }),
          })
        )
        .then(({ status, data }: { status: number; data: any }) => {
          if (String(status).startsWith(responseTypes.SUCCESS)) {
            if (data?.url) {
              window.location.replace(data.url);
            } else {
              navigate(routesPaths.MANAGEMENT_OVERVIEW);
            }
          } else if (data?.message_en) {
            openErrorModal({ text: data.message_en });
          } else {
            openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
          }
        })
        .catch((error: Error) => {
          log(error);
          openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
        });
    },
    [handleSocialLoginRequest, openErrorModal, t, log, queryParams]
  );

  const handleGoogleError = useCallback(
    (error: Error) => {
      log(error);
      openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
    },
    [log, openErrorModal, t]
  );

  const { googleButtonRef } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    text: GoogleButtonText.SIGN_IN_WITH,
  });

  return (
    <>
      <Helmet>
        <title>
          {passwordlessMode ? t('login-v2.passwordless-title') : t('login-v2.title')}
        </title>
      </Helmet>
      <StyledV2PageWrapper>
        <StyledV2PageBackground centered className="container">
          <StyledV2GlassCard>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
              <StyledLogo />
              <Typography variant="h2" textAlign={'center'}>
                {passwordlessMode ? t('login-v2.passwordless-title') : t('login-v2.title')}
              </Typography>
            </div>

            <form onSubmit={handleSubmit}>
              <StyledFormArea>
                <StyledFieldGroup>
                  <Typography variant={"body1Highlight" as any}>
                    {t('login-v2.email-address')}
                  </Typography>
                  <StyledV2Input
                    id="lv2-email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    hasError={touched.email && !!errors.email}
                    autoComplete="email"
                    autoFocus
                  />
                  {touched.email && errors.email && (
                    <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                      {errors.email}
                    </Typography>
                  )}
                </StyledFieldGroup>

                {!passwordlessMode && (
                  <StyledFieldGroup>
                    <Typography variant={"body1Highlight" as any}>
                      {t('common.password')}
                    </Typography>
                    <StyledV2Input
                      id="lv2-password"
                      name="password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasError={touched.password && !!errors.password}
                      autoComplete="current-password"
                    />
                    {touched.password && errors.password && (
                      <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                        {errors.password}
                      </Typography>
                    )}
                  </StyledFieldGroup>
                )}

                {!passwordlessMode && (
                  <StyledRememberRow>
                    <StyledV2Checkbox
                      id="lv2-remember"
                      type="checkbox"
                      name="rememberMe"
                      checked={values.rememberMe}
                      onChange={handleChange}
                    />
                    <Typography variant="body1" sx={{ flex: 1 }}>
                      {t('common.remember-me')}
                    </Typography>
                    <StyledV2LinkButton
                      type="button"
                      fontSize="16px"
                      onClick={handleTogglePasswordlessMode}
                      id={buttonIds.loginWithoutPassword}
                    >
                      {t('login-v2.passwordless-title')}
                    </StyledV2LinkButton>
                  </StyledRememberRow>
                )}

                <StyledV2SubmitBtn
                  type="submit"
                  disabled={isLoading}
                  id={buttonIds.login}
                >
                  {passwordlessMode ? t('common.continue-with-email') : t('common.login')}
                </StyledV2SubmitBtn>

                <StyledDividerRow>
                  <StyledDividerLine />
                  <Typography variant={'body1'}>
                    {t('common.or')}
                  </Typography>
                  <StyledDividerLine />
                </StyledDividerRow>

                {passwordlessMode && (
                  <>
                    <StyledSecondaryBtn
                      type="button"
                      disabled={isLoading}
                      onClick={() => navigate(routesPaths.FORGOT_PASSWORD_V2)}
                    >
                      {t('common.reset-password')}
                    </StyledSecondaryBtn>
                    <StyledSecondaryBtn
                      type="button"
                      disabled={isLoading}
                      onClick={handleTogglePasswordlessMode}
                    >
                      {t('common.login-with-password')}
                    </StyledSecondaryBtn>
                  </>
                )}

                {!passwordlessMode && (
                  <GoogleButton
                    buttonRef={googleButtonRef}
                    id={buttonIds.googleLogin}
                    disabled={isLoading}
                  />
                )}

                <Typography variant="body1" textAlign="center">
                  {t('common.dont-have-account')}{' '}
                  <StyledV2Link
                    href={links.SIGN_UP}
                    id={buttonIds.signUp}
                    onClick={handleSignupLinkClick(links.PRICING)}
                  >
                    {t('common.sign-up')}
                  </StyledV2Link>
                </Typography>
              </StyledFormArea>
            </form>
          </StyledV2GlassCard>
        </StyledV2PageBackground>
      </StyledV2PageWrapper>
    </>
  );
};

export const LoginV2 = withErrorHandling<unknown>(LoginV2Component);
